import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from './useApi';

const useSettings = () => {
  const { isAuthenticated } = useAuth();
  const { apiCall } = useApi();
  const [settings, setSettings] = useState({});
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);

  // Récupérer tous les paramètres
  const fetchSettings = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setLoading(true);
      setError(null);

      const response = await apiCall('/admin/settings');
      setSettings(response);
    } catch (err) {
      console.error('Erreur lors de la récupération des paramètres:', err);
      setError('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, apiCall]);

  // Récupérer les catégories de paramètres
  const fetchCategories = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await apiCall('/admin/settings/categories');
      setCategories(response);
    } catch (err) {
      console.error('Erreur lors de la récupération des catégories:', err);
    }
  }, [isAuthenticated, apiCall]);

  // Sauvegarder les paramètres
  const saveSettings = useCallback(async (newSettings) => {
    try {
      setSaveLoading(true);
      setError(null);

      const response = await apiCall('/admin/settings', {
        method: 'PUT',
        body: JSON.stringify(newSettings)
      });

      // Mettre à jour les paramètres localement
      setSettings(prevSettings => ({
        ...prevSettings,
        ...newSettings
      }));

      return response;
    } catch (err) {
      console.error('Erreur lors de la sauvegarde des paramètres:', err);
      setError('Erreur lors de la sauvegarde des paramètres');
      throw err;
    } finally {
      setSaveLoading(false);
    }
  }, [apiCall]);

  // Réinitialiser les paramètres
  const resetSettings = useCallback(async (category = null) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiCall('/admin/settings/reset', {
        method: 'POST',
        body: JSON.stringify({ category })
      });

      // Recharger tous les paramètres après réinitialisation
      await fetchSettings();

      return response;
    } catch (err) {
      console.error('Erreur lors de la réinitialisation:', err);
      setError('Erreur lors de la réinitialisation des paramètres');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiCall, fetchSettings]);

  // Mettre à jour un paramètre spécifique
  const updateSetting = useCallback((key, value) => {
    setSettings(prevSettings => ({
      ...prevSettings,
      [key]: value
    }));
  }, []);

  // Obtenir la valeur d'un paramètre
  const getSetting = useCallback((key, defaultValue = null) => {
    return settings[key] !== undefined ? settings[key] : defaultValue;
  }, [settings]);

  // Vérifier si un paramètre a été modifié
  const isSettingModified = useCallback((key, originalValue) => {
    return settings[key] !== originalValue;
  }, [settings]);

  // Charger les données initiales
  useEffect(() => {
    if (isAuthenticated) {
      fetchSettings();
      fetchCategories();
    }
  }, [isAuthenticated, fetchSettings, fetchCategories]);

  return {
    // Data
    settings,
    categories,
    loading,
    error,
    saveLoading,
    
    // Actions
    fetchSettings,
    saveSettings,
    resetSettings,
    updateSetting,
    
    // Utils
    getSetting,
    isSettingModified,
    isAuthenticated
  };
};

export default useSettings;